import { supabase } from "@/integrations/supabase/client";
import type { ApprovedCelebrationRecommendation } from "@/hooks/queries/useCelebrations";
import type { ApprovalRecommendation } from "@/components/concierge/ApprovalModal";
import { primarySkuIdFromLineItems } from "@/lib/celebrationCheckout";
import { resolveRecommendationSkuId } from "@shared/recommendation";
import {
  vendorProductToSkuCardData,
  type VendorProductCheckoutRow,
} from "@/lib/vendorProductToSkuCardData";
import type { SkuCardData } from "@/components/chat/SkuCard";

export type OpenCelebrationCheckoutResult =
  | { kind: "checkout"; sku: SkuCardData; context: CelebrationCheckoutSeed }
  | { kind: "pick_gift"; context: CelebrationPickGiftSeed }
  | { kind: "error"; message: string };

export interface CelebrationCheckoutSeed {
  celebrationId: string;
  recipientId: string;
  recipientName: string;
  occasion: string;
  defaultDate: string;
}

export interface CelebrationPickGiftSeed {
  celebrationId: string;
  recipientId: string;
  recipientName: string;
  occasion: string;
  defaultDate: string;
  dietaryRequirements?: string[];
}

/**
 * After approval, open checkout when we have a resolved SKU; otherwise
 * fall back to pick-gift (drawer / concierge) using the saved recommendation.
 */
export async function openApprovedCelebrationCheckout(
  celebrationId: string,
  companyId: string,
): Promise<OpenCelebrationCheckoutResult> {
  const { data, error } = await supabase
    .from("celebrations")
    .select(
      `id, date, type, team_member_id,
       team_members(id, name, dietary_requirements),
       celebration_recommendations(id, line_items, status, approved_at)`,
    )
    .eq("id", celebrationId)
    .eq("company_id", companyId)
    .maybeSingle();

  if (error || !data) {
    return { kind: "error", message: "Couldn't load that celebration. Try again." };
  }

  const tm = data.team_members;
  const memberRaw = Array.isArray(tm) ? tm[0] : tm;
  if (!memberRaw?.id) {
    return { kind: "error", message: "Couldn't resolve the recipient for checkout." };
  }

  const pickGiftSeed: CelebrationPickGiftSeed = {
    celebrationId: data.id,
    recipientId: memberRaw.id,
    recipientName: memberRaw.name,
    occasion: data.type,
    defaultDate: data.date,
    dietaryRequirements: memberRaw.dietary_requirements ?? undefined,
  };

  const recs = data.celebration_recommendations as
    | ApprovedCelebrationRecommendation[]
    | ApprovedCelebrationRecommendation
    | null;
  const recList = Array.isArray(recs) ? recs : recs ? [recs] : [];
  const approvedRec = recList
    .filter((r) => r.status === "approved")
    .sort((a, b) => (b.approved_at ?? "").localeCompare(a.approved_at ?? ""))[0];
  const skuId = primarySkuIdFromLineItems(approvedRec?.line_items ?? null);

  if (!skuId) {
    return { kind: "pick_gift", context: pickGiftSeed };
  }

  return hydrateSkuForCheckout(skuId, pickGiftSeed);
}

const VENDOR_PRODUCT_CHECKOUT_SELECT =
  "id, name, description, price, image_url, slug, vendor_category, lead_time_days, is_voucher, variant_label, dietary_tags, vendor_partners(vendor_name)";

async function hydrateSkuForCheckout(
  skuId: string,
  pickGiftSeed: CelebrationPickGiftSeed,
): Promise<OpenCelebrationCheckoutResult> {
  const { data: skuRow, error: skuErr } = await supabase
    .from("vendor_products")
    .select(VENDOR_PRODUCT_CHECKOUT_SELECT)
    .eq("id", skuId)
    .eq("is_available", true)
    .is("deleted_at", null)
    .maybeSingle();

  if (skuErr || !skuRow) {
    return { kind: "pick_gift", context: pickGiftSeed };
  }

  return {
    kind: "checkout",
    sku: vendorProductToSkuCardData(skuRow as VendorProductCheckoutRow),
    context: {
      celebrationId: pickGiftSeed.celebrationId,
      recipientId: pickGiftSeed.recipientId,
      recipientName: pickGiftSeed.recipientName,
      occasion: pickGiftSeed.occasion,
      defaultDate: pickGiftSeed.defaultDate,
    },
  };
}

/**
 * Dashboard Send on a drafted recommendation — resolve the picked SKU and
 * open OrderConfirmSheet (address, notes, delivery date) before payment.
 */
export async function openRecommendedCelebrationCheckout(input: {
  recommendation: ApprovalRecommendation;
  celebrationId: string;
  celebrationDate: string;
  celebrationType: string;
  recipientId: string;
  recipientName: string;
  dietaryRequirements?: string[];
}): Promise<OpenCelebrationCheckoutResult> {
  const pickGiftSeed: CelebrationPickGiftSeed = {
    celebrationId: input.celebrationId,
    recipientId: input.recipientId,
    recipientName: input.recipientName,
    occasion: input.celebrationType,
    defaultDate: input.celebrationDate,
    dietaryRequirements: input.dietaryRequirements,
  };

  const skuId = await resolveRecommendationSkuId(supabase, {
    skuId: input.recommendation.skuId,
    bakery: input.recommendation.bakery,
    productSummary: input.recommendation.productSummary,
    vendorCategory: input.recommendation.vendorCategory,
  });

  if (!skuId) {
    return { kind: "pick_gift", context: pickGiftSeed };
  }

  return hydrateSkuForCheckout(skuId, pickGiftSeed);
}

export interface ApplyCelebrationCheckoutHandlers {
  onCheckout: (sku: SkuCardData, context: CelebrationCheckoutSeed) => void;
  onPickGift: (context: CelebrationPickGiftSeed) => void;
  onError: (message: string) => void;
}

/** Wire `openApprovedCelebrationCheckout` into page-level checkout state. */
export function applyCelebrationCheckoutResult(
  result: OpenCelebrationCheckoutResult,
  handlers: ApplyCelebrationCheckoutHandlers,
): void {
  if (result.kind === "checkout") {
    handlers.onCheckout(result.sku, result.context);
    return;
  }
  if (result.kind === "pick_gift") {
    handlers.onPickGift(result.context);
    return;
  }
  handlers.onError(result.message);
}
